import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ConsoleShell from '../../components/admin2/ConsoleShell';
import { listDocuments } from '../../lib/docgen/store';
import { relativeTime } from '../../lib/relativeTime';

// Document generator — the list. Client quotations and supplier RFQs, drafts
// and approved, newest activity first. Admin-only (doc_* RLS); this page and
// the tool behind it write nothing to requests, offers or platform prices.

const KIND = { quotation: { ar: 'عرض سعر', en: 'Quotation' }, rfq: { ar: 'طلب تسعير (RFQ)', en: 'RFQ' } };
const STATUS = {
  draft: { ar: 'مسودة', en: 'Draft', tone: 'neutral' },
  approving: { ar: 'قيد الاعتماد', en: 'Approving', tone: 'warn' },
  approved: { ar: 'معتمد', en: 'Approved', tone: 'ok' },
  superseded: { ar: 'نسخة سابقة', en: 'Superseded', tone: 'neutral' },
};

export default function ConsoleDocs({ user, profile, lang }) {
  const isAr = lang === 'ar';
  const nav = useNavigate();
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState('');
  const [q, setQ] = useState('');
  const [kind, setKind] = useState('all');

  useEffect(() => {
    listDocuments().then(setRows).catch((e) => { setErr(e?.message || 'error'); setRows([]); });
  }, []);

  const who = (r) => r.content?.clientName || r.doc_clients?.name || '';
  const subject = (r) => r.content?.subject || r.content?.arSubtitle || r.content?.scopeText || '';

  const filtered = useMemo(() => {
    if (!rows) return [];
    const s = q.trim().toLowerCase();
    return rows.filter((r) => {
      if (kind !== 'all' && r.kind !== kind) return false;
      if (s && ![r.ref_no, who(r), subject(r), r.requests?.request_ref].filter(Boolean).join(' ').toLowerCase().includes(s)) return false;
      return true;
    });
  }, [rows, q, kind]);

  return (
    <ConsoleShell user={user} profile={profile} lang={lang} active="docs">
      <div className="ac-page" style={{ fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
        <div className="ac-page-head">
          <div>
            <h1 className="ac-h1">{isAr ? 'المستندات' : 'Documents'}</h1>
            <p className="ac-sub">{isAr ? 'عروض أسعار العملاء وطلبات التسعير للمصانع' : 'Client quotations and supplier RFQs'}</p>
          </div>
          <button className="ac-btn ac-btn-primary" onClick={() => nav('/admin2/docs/new')}>
            {isAr ? '+ مستند جديد' : '+ New document'}
          </button>
        </div>

        <input className="ac-input" style={{ maxWidth: 420, marginBottom: 12 }} value={q} onChange={(e) => setQ(e.target.value)}
          placeholder={isAr ? 'ابحث بالرقم أو العميل أو الموضوع…' : 'Search ref, client, subject…'} />
        <div className="ac-chiprow" style={{ marginBottom: 16 }}>
          {['all', 'quotation', 'rfq'].map((k) => (
            <button key={k} className={`ac-chip${kind === k ? ' on' : ''}`} onClick={() => setKind(k)}>
              {k === 'all' ? (isAr ? 'الكل' : 'All') : (isAr ? KIND[k].ar : KIND[k].en)}
            </button>
          ))}
        </div>

        {err && <div className="ac-placeholder" style={{ color: 'var(--ac-danger)' }}>{err}</div>}
        {rows == null ? (
          <div className="ac-skel" style={{ height: 90 }} />
        ) : filtered.length === 0 ? (
          <div className="ac-placeholder">{isAr ? 'لا مستندات بعد.' : 'No documents yet.'}</div>
        ) : filtered.map((r) => {
          const st = STATUS[r.status] || STATUS.draft;
          return (
            <button key={r.id} className="ac-card" onClick={() => nav(`/admin2/docs/${r.id}`)}
              style={{ display: 'block', width: '100%', textAlign: 'start', padding: 14, marginBottom: 10, cursor: 'pointer', font: 'inherit' }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <span style={{ fontSize: 14.5, fontWeight: 700, color: 'var(--ac-ink)', fontVariantNumeric: 'tabular-nums' }} dir="ltr">
                  {r.ref_no || (isAr ? 'مسودة' : 'Draft')}
                </span>
                <span className="ac-badge neutral">{isAr ? KIND[r.kind].ar : KIND[r.kind].en}</span>
                <span className={`ac-badge ${st.tone}`}><span className="dot" />{isAr ? st.ar : st.en}</span>
                {r.requests?.request_ref && <span className="ac-badge info">{r.requests.request_ref}</span>}
                {r.doc_clients && <span className="ac-badge info">{isAr ? 'عميل واتساب' : 'WhatsApp client'}</span>}
              </div>
              <div style={{ fontSize: 13, color: 'var(--ac-ink)', marginTop: 6 }}>
                {[who(r), subject(r)].filter(Boolean).join(' — ') || <span style={{ color: 'var(--ac-faint)' }}>{isAr ? 'بدون عنوان' : 'Untitled'}</span>}
              </div>
              <div style={{ fontSize: 11.5, color: 'var(--ac-muted)', marginTop: 4 }}>
                {isAr ? 'آخر تعديل ' : 'Updated '}{relativeTime(r.updated_at, isAr)}
              </div>
            </button>
          );
        })}
      </div>
    </ConsoleShell>
  );
}
